import { useState, useEffect } from 'react';
import { Icons, IconBtn, AvatarChip, Sheet, Field, PrimaryBtn, GhostBtn } from './UI';
import { STATUS_META, TEAM, fmtDate, today, nowISO, linkify } from '../utils/constants';
import { api } from '../utils/api';

// ── Add Incident Sheet ────────────────────────────────────────────────────────
function AddIncidentSheet({ initial, onSave, onClose, logLabel }) {
  const [note, setNote] = useState(initial?.note || '');
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial;

  const handleSave = async () => {
    if (!note.trim()) { alert('Please describe the incident.'); return; }
    setSaving(true);
    await onSave(note.trim());
    setSaving(false);
  };

  return (
    <Sheet
      title={isEdit ? `Edit ${logLabel || 'Incident'}` : `Log ${logLabel || 'Incident'}`}
      onClose={onClose}
      footer={
        <>
          <GhostBtn onClick={onClose}>Cancel</GhostBtn>
          <PrimaryBtn onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Log Incident'}
          </PrimaryBtn>
        </>
      }
    >
      <Field label="What happened?">
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={5}
          placeholder="Describe the incident, symptoms, time of occurrence, impact..."
          autoFocus
          style={{
            background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
            padding: '10px 12px', fontSize: 14, color: '#0f172a', outline: 'none',
            width: '100%', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
          }}
        />
      </Field>
      {!isEdit && (
        <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
          Your name and today's date will be added automatically.
        </div>
      )}
    </Sheet>
  );
}

// ── Tracker Detail ────────────────────────────────────────────────────────────
export function TrackerDetail({ tracker, currentUser, store, setStore, onEdit, onComplete, onDelete, onAddToToday, onBack }) {
  const [incidents, setIncidents] = useState(null);
  const [incNextId, setIncNextId] = useState(1);
  const [showAddIncident, setShowAddIncident] = useState(false);
  const [editingIncident, setEditingIncident] = useState(null);
  const [showAddToToday, setShowAddToToday] = useState(false);
  const [todayPerson, setTodayPerson] = useState(currentUser || '');
  const [todayNote, setTodayNote] = useState('');

  const sm = STATUS_META[tracker.status] || STATUS_META['Action Required'];

  useEffect(() => {
    api.getIncidents(tracker.id).then(data => {
      setIncidents(data.incidents || []);
      setIncNextId(data.nextId || 1);
    }).catch(() => setIncidents([]));
  }, [tracker.id]);

  const addIncident = async (note) => {
    const now = new Date();
    const d = now.toISOString().split('T')[0];
    const inc = {
      id: incNextId,
      trackerId: tracker.id,
      person: currentUser || '',
      date: d,
      note,
      createdAt: nowISO(),
    };
    setIncidents(prev => [...(prev || []), inc]);
    setIncNextId(n => n + 1);
    setShowAddIncident(false);
    await api.saveIncident(inc);
  };

  const editIncident = async (note) => {
    const updated = { ...editingIncident, note };
    setIncidents(prev => prev.map(i => i.id === updated.id ? updated : i));
    setEditingIncident(null);
    await api.saveIncident(updated);
  };

  const deleteIncident = async (id) => {
    if (!window.confirm('Delete this incident log entry?')) return;
    setIncidents(prev => prev.filter(i => i.id !== id));
    await api.deleteIncident(id);
  };

  const confirmAddToToday = async () => {
    if (!todayPerson) return;
    const newItem = {
      id: store.todayNextId || 1,
      person: todayPerson,
      date: today(),
      taskId: tracker.id,
      customText: '',
      note: todayNote.trim(),
      checked: false,
      sortOrder: store.todayNextId || 1,
    };
    setStore(s => ({ ...s, todayNextId: (s.todayNextId || 1) + 1, todayItems: [...(s.todayItems || []), newItem] }));
    setShowAddToToday(false);
    setTodayNote('');
    await api.saveTodayItem(newItem);
    alert(`Added to ${todayPerson === currentUser ? 'your' : todayPerson + "'s"} Today list!`);
  };

  return (
    <div style={{ padding: 16, maxWidth: 680, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: '#3b82f6', cursor: 'pointer', fontWeight: 600, fontSize: 15, display: 'flex', alignItems: 'center', gap: 4 }}>
          <Icons.Back /> Back
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowAddToToday(true)} style={{ background: '#7c3aed', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>☑ Add to Today</button>
          <button onClick={() => onEdit(tracker)} style={{ background: '#3b82f6', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600 }}>Edit</button>
        </div>
      </div>

      {/* Type badge */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' }}>📊 {tracker.subtype === 'tracker-task' ? 'Task Tracker' : 'Incident Tracker'}</span>
        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, background: sm.bg, color: sm.color }}>{sm.icon} {tracker.status}</span>
      </div>

      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: '0 0 8px', lineHeight: 1.3 }}>{tracker.title}</h2>

      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <span>Opened {fmtDate(tracker.dateAdded)}</span>
        {tracker.dateResolved && <><span style={{ color: '#cbd5e1' }}>·</span><span>Resolved {fmtDate(tracker.dateResolved)}</span></>}
      </div>

      {/* Assignees */}
      {tracker.assignees?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={sectionLabel}>Assigned To</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {tracker.assignees.map(a => <AvatarChip key={a} name={a} />)}
          </div>
        </div>
      )}

      {/* Description */}
      {tracker.notes && (
        <div style={{ marginBottom: 20 }}>
          <div style={sectionLabel}>Description</div>
          <div style={{ fontSize: 14, color: '#334155', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: linkify(tracker.notes) }} />
        </div>
      )}

      {/* Incident Log */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={sectionLabel}>{tracker.subtype === 'tracker-task' ? 'Step Log' : 'Incident Log'} {incidents ? `(${incidents.length})` : ''}</div>
          {tracker.status !== 'Complete' && (
            <button onClick={() => setShowAddIncident(true)} style={{ background: '#dc2626', border: 'none', color: '#fff', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 5 }}>
              + {tracker.subtype === 'tracker-task' ? 'Log Step' : 'Log Incident'}
            </button>
          )}
        </div>

        {incidents === null && <div style={{ color: '#94a3b8', fontSize: 14 }}>Loading...</div>}
        {incidents && incidents.length === 0 && (
          <div style={{ color: '#94a3b8', fontSize: 14, fontStyle: 'italic', padding: '12px 0' }}>No incidents logged yet.</div>
        )}
        {incidents && incidents.map((inc, idx) => (
          <div key={inc.id} style={{ display: 'flex', gap: 12, marginBottom: 12, paddingBottom: 12, borderBottom: idx < incidents.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
            <div style={{ flexShrink: 0, width: 4, background: '#dc2626', borderRadius: 2, minHeight: 40 }} />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>
                  {inc.person} · {fmtDate(inc.date)}
                </div>
                <div style={{ display: 'flex', gap: 2 }} onClick={e => e.stopPropagation()}>
                  <IconBtn onClick={() => setEditingIncident(inc)} title="Edit"><Icons.Edit /></IconBtn>
                  <IconBtn onClick={() => deleteIncident(inc.id)} title="Delete" color="#ef4444"><Icons.X /></IconBtn>
                </div>
              </div>
              <div style={{ fontSize: 14, color: '#0f172a', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: linkify(inc.note) }} />
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      {tracker.status !== 'Complete' && (
        <button onClick={() => onComplete(tracker.id)} style={{ background: '#22c55e', border: 'none', color: '#fff', borderRadius: 8, padding: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14, width: '100%', marginTop: 8 }}>
          ✅ Mark as Resolved
        </button>
      )}
      <button onClick={() => onDelete(tracker.id)} style={{ background: 'transparent', border: '1px solid #fca5a5', color: '#ef4444', borderRadius: 8, padding: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14, width: '100%', marginTop: 8 }}>
        🗑 Delete Tracker
      </button>

      {/* Add / Edit Incident Sheet */}
      {showAddIncident && <AddIncidentSheet onSave={addIncident} onClose={() => setShowAddIncident(false)} logLabel={tracker.subtype === 'tracker-task' ? 'Step' : 'Incident'} />}
      {editingIncident && <AddIncidentSheet initial={editingIncident} onSave={editIncident} onClose={() => setEditingIncident(null)} logLabel={tracker.subtype === 'tracker-task' ? 'Step' : 'Incident'} />}

      {/* Add to Today Sheet */}
      {showAddToToday && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 200 }} onClick={e => e.target === e.currentTarget && setShowAddToToday(false)}>
          <div style={{ background: '#fff', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 640, display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 16px 12px', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: 16, fontWeight: 700 }}>Add to Today's List</span>
              <button onClick={() => setShowAddToToday(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 20, color: '#64748b' }}>×</button>
            </div>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 14, color: '#475569', background: '#f8fafc', borderRadius: 8, padding: '10px 12px', borderLeft: '3px solid #c2410c' }}>{tracker.title}</div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Add to whose list?</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {TEAM.map(name => (
                    <button key={name} onClick={() => setTodayPerson(name)} style={{ background: todayPerson === name ? '#dbeafe' : '#f1f5f9', border: `1px solid ${todayPerson === name ? '#93c5fd' : '#e2e8f0'}`, color: todayPerson === name ? '#1d4ed8' : '#475569', borderRadius: 20, padding: '5px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
                      {name}{name === currentUser ? ' (me)' : ''}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Focus note (optional)</div>
                <textarea value={todayNote} onChange={e => setTodayNote(e.target.value)} rows={3} placeholder="What specifically should they do with this tracker today?" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', fontSize: 14, color: '#0f172a', outline: 'none', width: '100%', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderTop: '1px solid #f1f5f9' }}>
              <button onClick={() => setShowAddToToday(false)} style={{ background: '#f1f5f9', border: 'none', color: '#475569', borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>Cancel</button>
              <button onClick={confirmAddToToday} style={{ background: '#3b82f6', border: 'none', color: '#fff', borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 14, flex: 1 }}>
                ☑ Add to {todayPerson === currentUser ? 'My' : todayPerson + "'s"} Today
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const sectionLabel = { fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 };
